import { Link } from 'react-router-dom';
import { Banknote, CreditCard, ReceiptText } from 'lucide-react';

import { formatUsd } from '@/lib/format';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { StatusBadge, paymentStatusBadge } from '@/components/ui/StatusBadge';
import { usePayments, type PaymentRow } from '@/hooks/usePayments';
import CreditsPanel from '@/components/billing/CreditsPanel';

/**
 * /payments — the customer's payment history / saved receipts. Every payment
 * we've processed for them, newest first, with the invoices it was applied to.
 * Rendered through the shared DataTable + StatusBadge so it matches every
 * other list in the portal.
 */

function toNum(v: number | string): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function AppliedTo({ p }: { p: PaymentRow }) {
  const allocations = p.payment_invoice_allocations ?? [];
  if (allocations.length === 0) return <span className="text-brix-muted">—</span>;
  return (
    <div className="space-y-0.5">
      {allocations.map((a) => (
        <div key={a.qbo_invoice_id} className="flex items-center justify-end gap-2 text-sm">
          <Link
            to={`/invoices/${encodeURIComponent(a.qbo_invoice_id)}`}
            className="text-brix-primary hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            #{a.qbo_invoice_id}
          </Link>
          <span className="tabular-nums text-slate-500">{formatUsd(toNum(a.amount))}</span>
        </div>
      ))}
    </div>
  );
}

export default function Payments() {
  const { data, isLoading, error } = usePayments();

  const columns: Column<PaymentRow>[] = [
    {
      key: 'date',
      header: 'Payment',
      sortValue: (p) => p.created_at,
      cell: (p) => (
        <div className="flex items-center gap-3">
          {p.type === 'ach' ? (
            <Banknote size={18} className="shrink-0 text-brix-primary" />
          ) : (
            <CreditCard size={18} className="shrink-0 text-brix-primary" />
          )}
          <div>
            <span className="block font-semibold text-slate-900">{fmtDate(p.created_at)}</span>
            {p.initiated_by === 'autopay' && (
              <span className="block text-xs text-brix-muted">Autopay</span>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'applied',
      header: 'Applied to',
      align: 'right',
      cell: (p) => <AppliedTo p={p} />,
      hideOnMobile: true,
    },
    {
      key: 'status',
      header: 'Status',
      align: 'center',
      cell: (p) => {
        const s = paymentStatusBadge(p.status);
        return <StatusBadge tone={s.tone}>{s.label}</StatusBadge>;
      },
    },
    {
      key: 'amount',
      header: 'Amount',
      numeric: true,
      sortValue: (p) => toNum(p.amount),
      width: '130px',
      cell: (p) => (
        <span className="text-base font-bold text-slate-900">{formatUsd(toNum(p.amount))}</span>
      ),
    },
  ];

  return (
    <div className="scrollbar-thin h-full overflow-y-auto pb-20 md:pb-10">
      <div className="mx-auto max-w-3xl space-y-5 px-4 pb-12 pt-6 md:px-8">
        <div>
          <h1 className="flex items-center gap-2 font-display text-xl font-bold tracking-tight text-brix-ink md:text-2xl">
            <ReceiptText size={22} className="text-brix-primary" /> Payments &amp; credits
          </h1>
          <p className="mt-1 text-base text-brix-muted">
            What you've paid us, and any money on your account that isn't on an invoice yet.
          </p>
        </div>

        <CreditsPanel />

        <div className="pt-2">
          <h2 className="font-display text-base font-bold tracking-tight text-brix-ink">
            Paid through the portal
          </h2>
          <p className="mt-1 text-sm text-brix-muted">
            Card and bank payments made here, newest first — a receipt is emailed each time.
            Cheques and bank transfers you send us are recorded in QuickBooks and show above once
            they're applied.
          </p>
        </div>

        <DataTable
          columns={columns}
          rows={data}
          rowKey={(p) => p.id}
          isLoading={isLoading}
          error={error instanceof Error ? error.message : error ? 'Failed to load payments.' : null}
          initialSort={{ key: 'date', dir: 'desc' }}
          caption="Payment history"
          empty={
            <>
              No payments yet. Payments you make on the{' '}
              <Link to="/pay" className="font-semibold text-brix-primary hover:underline">
                Pay
              </Link>{' '}
              page will appear here.
            </>
          }
          mobileRow={(p) => {
            const s = paymentStatusBadge(p.status);
            return (
              <div className="flex items-start gap-3">
                {p.type === 'ach' ? (
                  <Banknote size={18} className="mt-0.5 shrink-0 text-brix-primary" />
                ) : (
                  <CreditCard size={18} className="mt-0.5 shrink-0 text-brix-primary" />
                )}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold text-slate-900">{fmtDate(p.created_at)}</span>
                    <span className="text-base font-bold tabular-nums text-slate-900">
                      {formatUsd(toNum(p.amount))}
                    </span>
                  </div>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <StatusBadge tone={s.tone} size="sm">{s.label}</StatusBadge>
                    {p.initiated_by === 'autopay' && (
                      <span className="text-xs text-brix-muted">Autopay</span>
                    )}
                  </div>
                  {(p.payment_invoice_allocations ?? []).length > 0 && (
                    <div className="mt-2 border-t border-brix-border pt-2">
                      <AppliedTo p={p} />
                    </div>
                  )}
                </div>
              </div>
            );
          }}
        />
      </div>
    </div>
  );
}
